import { maybeAddZeroBeforeNumber, numberToLetter } from '@dealstack/shared/lib/format/number';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { isString } from 'lodash';

export const normalizeScreamingSnakeToUI = (text: string | null) => {
	const normalized = (text ?? '').replace(/_/g, ' ').toLowerCase();
	return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

export const normalizeFileNameToUI = (text?: string): string | null => {
	if (!text) {
		return null;
	}
	const split = text.split('.');

	if (split.length === 1) {
		// No extension found, return original text
		return text;
	}

	split.pop();
	return split.join('.');
};

//remove phone prefix and all non digits characters
export const normalizePhoneNumberForBE = <T extends string | undefined>(
	fullNumber: T,
	prefix?: string
): T => {
	if (!fullNumber) {
		return fullNumber; // Return as it is if it's undefined
	}

	if (!prefix) {
		// Remove the prefix from the full number
		return fullNumber;
	}
	const parsedPhoneNumber = parsePhoneNumberFromString(fullNumber);
	const formattedPhoneNumber = parsedPhoneNumber?.format('INTERNATIONAL');

	// Format before slice, making sure we remove 0 from numbers like +44 07496 444 121 will be changed to +44 7496 444 121
	return formattedPhoneNumber?.slice(prefix.length).replace(/\D/g, '') as T;
};

export const normalizePhoneNumberForFormValue = (
	phoneNumber?: string | null,
	prefix?: string | null
) => {
	if (!phoneNumber) {
		return;
	}
	if (!prefix) {
		return phoneNumber;
	}
	return `${prefix}${phoneNumber}`;
};

//remove non digits characters but include + sign
export const normalizePhoneNumberForFormValueComparison = (phoneNumber: string) => {
	if (!phoneNumber) {
		return '';
	}
	return phoneNumber.replace(/[^+0-9]/g, '');
};

export const IBAN_COUNTRY_CODE_LENGTH = 2;
export const getAccountNumberFromIban = (iban: string) => {
	return iban && iban.length > IBAN_COUNTRY_CODE_LENGTH
		? iban.substring(IBAN_COUNTRY_CODE_LENGTH)
		: '';
};

export const changeTextFormat = (text: string) => {
	if (!text) {
		return;
	}

	const textWithoutUnderscore = text.replaceAll('_', ' ');
	const textToLower = textWithoutUnderscore.toLowerCase();
	const firstLetterToUpper = textToLower.charAt(0).toUpperCase();
	const rest = textToLower.slice(1);
	const formattedText = firstLetterToUpper + rest;

	return formattedText;
};

export function lowerCaseTrimString(text: string): string;
export function lowerCaseTrimString(text?: string): string | undefined;
export function lowerCaseTrimString(text?: string | null): string | undefined;
export function lowerCaseTrimString(text?: string | null): string | undefined {
	return text?.toLowerCase()?.trim();
}

export const isSameStringKeyInsensitive = (input1: string, input2: string): boolean => {
	if (!input1 || !input2) {
		return false;
	}
	return lowerCaseTrimString(input1) === lowerCaseTrimString(input2);
};

export const truncateString = (input: string, truncateFrom: number, truncateTo: number) => {
	return input.length <= truncateTo ? input : input.substring(truncateFrom, truncateTo);
};

export const getStepsIndication = (
	activeMainStepIndex?: number,
	activeSubStepIndex?: number,
	totalLength?: number
): string => {
	if (activeMainStepIndex == null || totalLength == null) {
		return '';
	}

	const subStepIndication =
		activeSubStepIndex != null ? `(${numberToLetter(activeSubStepIndex + 1)})` : '';

	return `${maybeAddZeroBeforeNumber(activeMainStepIndex + 1)}${subStepIndication} of ${maybeAddZeroBeforeNumber(
		totalLength
	)}`;
};

export const defaultToEmptyString = (value?: string) => value ?? '';

export const getUserFullName = (
	user?: {
		firstName: string | null;
		lastName: string | null;
	} | null
): string => {
	return `${user?.firstName ?? ''} ${user?.lastName ?? ''}`;
};

export const addPrefix = (prefix: string, input: string): string => `${prefix}${input}`;

export const removePrefix = (prefix: string, input: string): string => input.replace(prefix, '');

type TextChunk = {
	text: string;
	isBold: boolean;
};
/**
 * Splits a string containing bold tags ("&lt;b&gt;" and "&lt;/b&gt;") into an array of text chunks.
 * Each chunk represents a segment of the text, with a boolean indicating whether the text is bold.
 *
 * @param {string} text - The input string, e.g., "This is &lt;b&gt;bold&lt;/b&gt; and this is not."
 * @returns {TextChunk[]} An array of text chunks, e.g.,
 * [
 *   { text: 'This is ', isBold: false },
 *   { text: 'bold', isBold: true },
 *   { text: ' and this is not.', isBold: false }
 * ]
 */
export const splitStringToBoldChunks = (text: string): TextChunk[] => {
	const boldTagRegex = /(<b>.*?<\/b>|[^<]+)/g;
	const chunks: TextChunk[] = [];
	let match: RegExpExecArray | null;

	while ((match = boldTagRegex.exec(text)) !== null) {
		const chunk = match[0];
		if (chunk.startsWith('<b>') && chunk.endsWith('</b>')) {
			chunks.push({ text: chunk.slice(3, -4), isBold: true });
		} else {
			chunks.push({ text: chunk, isBold: false });
		}
	}

	return chunks;
};

export const replaceSpaceWithUnderscore = (value?: string | null): string => {
	if (!value) {
		return '';
	}
	return value.replace(/\s+/g, '_');
};

export const isNonEmptyString = (value: unknown): boolean => {
	return isString(value) && value.trim().length > 0;
};

/**
 * Drops zero-width and bidi-control code points — the characters a string can carry without them
 * showing.
 *
 * A translated phrase is not always the word it looks like: Tolgee appends a run of zero-width
 * joiners to every phrase it serves for in-context editing, so a phrase reading as "delete" can hold
 * thirty-odd code points. Anything comparing a translated string against text a user typed has to
 * compare the visible characters — the invisible run cannot be typed.
 */
export const stripInvisibleCharacters = (value: string): string =>
	// Zero-width space/joiners and bidi marks (200B-200F), word joiner and the invisible maths
	// operators (2060-2064), and the byte-order mark (FEFF).
	value.replace(/[\u200B-\u200F\u2060-\u2064\uFEFF]/g, '');
